import type { LucideIcon } from 'lucide-react'
import {
  Home,
  Layers,
  // Workflow, // re-enable with the nav.pipelineDesigner item below
  Brain,
  ActivitySquare,
  Database,
  Wrench,
  Library,
  ShieldCheck,
  FileText,
  HardDrive,
  Sparkles,
  Building2,
  Store,
  ShoppingBag,
  // GitBranch, // re-enable with the nav.dataPipelines item below
  Activity,
  Lightbulb,
  LayoutGrid,
  LayoutTemplate,
  Network,
  Box,
  BarChart3,
  ArrowRightLeft,
  BookOpen,
  Bot,
  Waypoints,
} from 'lucide-react'
import type { FeatureFlagKey } from './featureFlags'

export interface NavItem {
  labelKey: string
  path: string
  icon: LucideIcon
  /** App modes where this item should receive the active state */
  activeModes?: Array<'platform' | 'milkyway' | 'dataflow'>
  /**
   * Feature flag key for view access.
   * This determines if the nav item is visible.
   * Combined with role permissions in AppSidebar.
   */
  featureFlag?: FeatureFlagKey
  /**
   * When true, `featureFlag` is evaluated against the REAL tenant flag value
   * (bypassing the global isEnabled kill-switch in FeatureFlagsContext) so the
   * item is hidden whenever the flag is actually disabled. Opt-in per item;
   * most items intentionally ignore flags and rely on RBAC only.
   */
  enforceFeatureFlag?: boolean
  /**
   * Feature ID for role permission check.
   * Used to check if user's role has 'view' permission.
   */
  featureId?: string
  /**
   * Sub-feature key for role permission check (within a scoped feature).
   * When set, the eye-toggle is read at `<featureId>.<subFeatureId>.view_actions`
   * instead of the parent feature. Must match a child `service` in policyUtils
   * SCOPED_FEATURES_DATA (e.g. for `ontology`: canvas | data_models | kpi_library
   * | pipelines | observability | migrations).
   */
  subFeatureId?: string
  /** If true, requires platform admin role */
  platformAdmin?: boolean
  /**
   * If true, requires an admin-named role (or platform admin) on top of the
   * `featureFlag` tenant gate. Admin is not a scoped feature, so it has no
   * per-role eye-toggle — this mirrors AdminPage's route guard so regular
   * users don't see the entry just because the tenant flag is on.
   */
  adminRole?: boolean
}

export interface NavSection {
  id: string
  /** Translation key for section header. Undefined means no header (top-level items) */
  labelKey?: string
  items: NavItem[]
}

/**
 * Navigation sections with grouped items
 *
 * Structure:
 * - Top Level: Home, Solutions, Agent Store, Workflow Store
 * - BLUEPRINTS: Blueprints library, Decision Intelligence Platform (/dip)
 * - AGENT DEVELOP: Projects, Data, Prompts, Skills, Agent Studio, Agent Workflows
 * - OBSERVABILITY: Agent Registry, AI Transparency, Evals and Simulations, Monitoring (cache dashboard lives under Monitoring when enabled)
 * - AGENT OPS: Models, Tools, Connections
 * - RESOURCES: Docs (embedded product documentation)
 * - ADMIN: Admin, Platform Admin
 *
 * Each item has:
 * - featureFlag: The flag key to check (e.g., 'projects_view')
 * - featureId: The feature for role permission check (e.g., 'projects')
 */
export const NAV_SECTIONS: NavSection[] = [
  {
    id: 'top',
    items: [
      {
        labelKey: 'nav.home',
        path: '/',
        icon: Home,
        featureFlag: 'home_view',
        featureId: 'home',
      },
      {
        labelKey: 'nav.solutions',
        path: '/solutions',
        icon: LayoutGrid,
        featureFlag: 'solutions_view',
        featureId: 'solutions',
      },
      {
        labelKey: 'nav.agentStore',
        path: '/agent-store',
        icon: Store,
        featureFlag: 'agent_store_view',
        featureId: 'agent-store',
      },
      {
        labelKey: 'nav.workflowStore',
        path: '/workflow-store',
        icon: ShoppingBag,
        featureFlag: 'workflow_store_view',
        featureId: 'workflow-store',
      },
    ],
  },
  {
    id: 'ai_blueprints',
    labelKey: 'nav.sections.aiBlueprints',
    items: [
      {
        labelKey: 'nav.blueprints',
        path: '/blueprints',
        icon: LayoutTemplate,
        featureFlag: 'ai_blueprints_view',
        featureId: 'ai-blueprints',
      },
      {
        labelKey: 'nav.autopilot',
        path: '/ideate',
        icon: Lightbulb,
        featureFlag: 'ideate_view',
        featureId: 'ideate',
      },
    ],
  },
  {
    // Every Enterprise Ontology item sets `enforceFeatureFlag` so the child
    // `ontology_*_view` tenant flag is read for real. Without it these items
    // route through useCanAccess.isFlagEnabled (hardcoded `true`) and stay
    // visible no matter how the flags are toggled. The parent `ontology_view`
    // is already enforced separately by canViewUI.
    id: 'enterprise_ontology',
    labelKey: 'nav.sections.enterpriseOntology',
    items: [
      {
        labelKey: 'nav.migrationsStudio',
        path: '/ontology/migrations',
        icon: ArrowRightLeft,
        featureFlag: 'ontology_migrations_view',
        enforceFeatureFlag: true,
        featureId: 'ontology',
        subFeatureId: 'migrations',
      },
      {
        labelKey: 'nav.dataModels',
        path: '/ontology/data-models',
        icon: Box,
        featureFlag: 'ontology_data_models_view',
        enforceFeatureFlag: true,
        featureId: 'ontology',
        subFeatureId: 'data_models',
      },
      {
        labelKey: 'nav.ontologyCanvas',
        path: '/ontology',
        icon: Network,
        featureFlag: 'ontology_canvas_view',
        enforceFeatureFlag: true,
        featureId: 'ontology',
        subFeatureId: 'canvas',
      },
      {
        labelKey: 'nav.kpiLibrary',
        path: '/ontology/kpi-library',
        icon: BarChart3,
        featureFlag: 'ontology_kpi_library_view',
        enforceFeatureFlag: true,
        featureId: 'ontology',
        subFeatureId: 'kpi_library',
      },
      // {
      //   labelKey: 'nav.dataPipelines',
      //   path: '/ontology/pipelines',
      //   icon: GitBranch,
      //   featureFlag: 'ontology_pipelines_view',
      //   enforceFeatureFlag: true,
      //   featureId: 'ontology',
      //   subFeatureId: 'pipelines',
      // },
      // {
      //   labelKey: 'nav.pipelineDesigner',
      //   path: '/pipeline-designer',
      //   icon: Workflow,
      //   featureFlag: 'ontology_pipeline_designer_view',
      //   enforceFeatureFlag: true,
      //   featureId: 'ontology',
      //   subFeatureId: 'pipelines',
      // },
      // {
      //   // Keeps `data_view` as its own flag (this is the Data page under an
      //   // ontology entry point); the ontology gate still applies through the
      //   // `ontology`/`pipelines` RBAC check, which enforces `ontology_view`.
      //   labelKey: 'nav.dataExplorer',
      //   path: '/data-explorer',
      //   icon: HardDrive,
      //   featureFlag: 'data_view',
      //   enforceFeatureFlag: true,
      //   featureId: 'ontology',
      //   subFeatureId: 'pipelines',
      //   activeModes: ['platform', 'dataflow'],
      // },
      // {
      //   labelKey: 'nav.dataObservability',
      //   path: '/ontology/observability',
      //   icon: Activity,
      //   featureFlag: 'ontology_observability_view',
      //   enforceFeatureFlag: true,
      //   featureId: 'ontology',
      //   subFeatureId: 'observability',
      // },
    ],
  },
  {
    id: 'develop',
    labelKey: 'nav.sections.agentDevelop',
    items: [
      {
        labelKey: 'nav.projects',
        path: '/projects',
        icon: Layers,
        featureFlag: 'projects_view',
        featureId: 'projects',
      },
      {
        labelKey: 'nav.data',
        path: '/data',
        icon: HardDrive,
        featureFlag: 'data_view',
        featureId: 'data',
        activeModes: ['platform'],
      },
      {
        labelKey: 'nav.prompts',
        path: '/prompts',
        icon: FileText,
        featureFlag: 'prompts_view',
        featureId: 'prompts',
      },
      {
        labelKey: 'nav.skills',
        path: '/skills',
        icon: Sparkles,
        featureFlag: 'skills_view',
        featureId: 'skills',
      },
      {
        labelKey: 'nav.agentStudio',
        path: '/agent-studio',
        icon: Bot,
        featureFlag: 'agent_studio_view',
        featureId: 'agent-studio',
      },
      {
        labelKey: 'nav.agentWorkflows',
        path: '/agent-workflows',
        icon: Waypoints,
        featureFlag: 'agent_workflows_view',
        featureId: 'agent-workflows',
      },
    ],
  },
  {
    id: 'observability',
    labelKey: 'nav.sections.observability',
    items: [
      {
        labelKey: 'nav.registryUnified',
        path: '/agent-registry',
        icon: Brain,
        featureFlag: 'agent_registry_view',
        featureId: 'agent-registry',
      },
      {
        labelKey: 'nav.aiTransparency',
        path: '/ai-transparency',
        icon: Network,
        featureFlag: 'ai_transparency_view',
        enforceFeatureFlag: true,
        // Reuses Agent Registry's RBAC grant — same underlying registry data, view-only.
        featureId: 'agent-registry',
      },
      {
        labelKey: 'nav.evalsSimulations',
        path: '/evals-simulations',
        icon: ActivitySquare,
        featureFlag: 'evals_simulations_view',
        featureId: 'evals-simulations',
      },
      {
        labelKey: 'nav.monitoring',
        path: '/monitoring',
        icon: Activity,
        featureFlag: 'monitoring_view',
        featureId: 'monitoring',
      },
    ],
  },
  {
    id: 'ops',
    labelKey: 'nav.sections.agentOps',
    items: [
      {
        labelKey: 'nav.models',
        path: '/models',
        icon: Database,
        featureFlag: 'models_view',
        featureId: 'models',
      },
      {
        labelKey: 'nav.tools',
        path: '/tools',
        icon: Wrench,
        featureFlag: 'tools_view',
        featureId: 'tools',
      },
      {
        labelKey: 'nav.connections',
        path: '/connections',
        icon: Library,
        featureFlag: 'connections_view',
        featureId: 'connections',
      },
    ],
  },
  {
    id: 'resources',
    labelKey: 'nav.sections.resources',
    items: [
      {
        labelKey: 'nav.docs',
        path: '/resources/docs',
        icon: BookOpen,
        featureFlag: 'docs_view',
        // Docs visibility honours the real docs_view tenant flag (not just RBAC),
        // so disabling the flag hides the item and locks the route.
        enforceFeatureFlag: true,
        featureId: 'docs',
      },
    ],
  },
  {
    id: 'admin',
    labelKey: 'nav.sections.admin',
    items: [
      {
        // Admin section has no scoped policy in the registry, so there is no
        // per-role eye-toggle. Gate it on an admin-named role (or platform
        // admin) AND the `admin_view` tenant flag — matching AdminPage's route
        // guard — so it does not show for regular users. Backend routes still
        // enforce per-tab permissions (admin_users_view, admin_roles_view, etc.).
        labelKey: 'nav.admin',
        path: '/admin',
        icon: ShieldCheck,
        featureFlag: 'admin_view',
        adminRole: true,
      },
      {
        // Platform admin requires the platform_admin role plus the
        // `platform_admin_view` deployment/tenant flag (from master YAML).
        labelKey: 'nav.platformAdmin',
        path: '/platform-admin',
        icon: Building2,
        platformAdmin: true,
        featureFlag: 'platform_admin_view',
        enforceFeatureFlag: true,
      },
    ],
  },
]

/**
 * Milkyway (Business User) navigation -- minimalist view.
 * No feature flags required; business users see these unconditionally.
 */
export const MILKYWAY_NAV_SECTIONS: NavSection[] = [
  {
    id: 'milkyway_main',
    items: [
      {
        labelKey: 'nav.autopilot',
        path: '/ideate',
        icon: Lightbulb,
      },
    ],
  },
]

/**
 * Valid paths in Milkyway mode (used for smart redirect on mode switch).
 */
export const MILKYWAY_NAV_PATHS: string[] = MILKYWAY_NAV_SECTIONS.flatMap((s) =>
  s.items.map((i) => i.path)
)

/**
 * Milky Way Ontology Studio navigation -- Enterprise Ontology focused view.
 */
export const DATAFLOW_NAV_SECTIONS: NavSection[] = [
  {
    id: 'dataflow_main',
    labelKey: 'nav.sections.enterpriseOntology',
    items: [
      {
        labelKey: 'nav.migrationsStudio',
        path: '/ontology/migrations',
        icon: ArrowRightLeft,
        featureFlag: 'ontology_migrations_view',
        enforceFeatureFlag: true,
        featureId: 'ontology',
        subFeatureId: 'migrations',
      },
      {
        labelKey: 'nav.dataModels',
        path: '/ontology/data-models',
        icon: Box,
        featureFlag: 'ontology_data_models_view',
        enforceFeatureFlag: true,
        featureId: 'ontology',
        subFeatureId: 'data_models',
      },
      {
        labelKey: 'nav.ontologyCanvas',
        path: '/ontology',
        icon: Network,
        featureFlag: 'ontology_canvas_view',
        enforceFeatureFlag: true,
        featureId: 'ontology',
        subFeatureId: 'canvas',
      },
      {
        labelKey: 'nav.kpiLibrary',
        path: '/ontology/kpi-library',
        icon: BarChart3,
        featureFlag: 'ontology_kpi_library_view',
        enforceFeatureFlag: true,
        featureId: 'ontology',
        subFeatureId: 'kpi_library',
      },
      // {
      //   labelKey: 'nav.dataPipelines',
      //   path: '/ontology/pipelines',
      //   icon: GitBranch,
      //   featureFlag: 'ontology_pipelines_view',
      //   enforceFeatureFlag: true,
      //   featureId: 'ontology',
      //   subFeatureId: 'pipelines',
      // },
      // {
      //   labelKey: 'nav.dataExplorer',
      //   path: '/data-explorer',
      //   icon: HardDrive,
      //   featureFlag: 'data_view',
      //   enforceFeatureFlag: true,
      //   featureId: 'ontology',
      //   subFeatureId: 'pipelines',
      // },
      // {
      //   labelKey: 'nav.dataObservability',
      //   path: '/ontology/observability',
      //   icon: Activity,
      //   featureFlag: 'ontology_observability_view',
      //   enforceFeatureFlag: true,
      //   featureId: 'ontology',
      //   subFeatureId: 'observability',
      // },
    ],
  },
]

/**
 * Valid paths in DataStudio mode (used for smart redirect on mode switch).
 */
export const DATAFLOW_NAV_PATHS: string[] = DATAFLOW_NAV_SECTIONS.flatMap((s) =>
  s.items.map((i) => i.path)
)

/**
 * Flat list of all nav items (for backward compatibility)
 * @deprecated Use NAV_SECTIONS instead for grouped navigation
 */
export const NAV_ITEMS: NavItem[] = NAV_SECTIONS.flatMap((section) => section.items)
